// TanStack Query hooks for the DSR Admin module — internal user accounts
// (settings.manage_users) and role/permission administration
// (settings.manage_rbac). Mirrors packages/api/src/routes/admin-users.ts and
// admin-rbac.ts. Follows the standard list/detail/mutation pattern.

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AdminUserResponse,
  AdminUsersListResponse,
  CreateAdminUserInput,
  CreateAdminUserResponse,
  CreateRoleInput,
  PermissionsListResponse,
  ResetPasswordInput,
  ResetPasswordResponse,
  RevokeSessionsResponse,
  RoleResponse,
  RolesListResponse,
  UpdateAdminUserInput,
  UpdateRoleInput,
} from '@dsr/shared-types';
import { api } from '../client';

const KEY = 'admin';

export const adminKeys = {
  users: () => [KEY, 'users'] as const,
  usersList: (filters: AdminUsersFilters) => [KEY, 'users', 'list', { ...filters }] as const,
  user: (id: number) => [KEY, 'users', 'detail', id] as const,
  roles: () => [KEY, 'roles'] as const,
  role: (id: number) => [KEY, 'roles', 'detail', id] as const,
  permissions: () => [KEY, 'permissions'] as const,
};

function invalidateUser(qc: QueryClient, id: number): void {
  void qc.invalidateQueries({ queryKey: adminKeys.users() });
  void qc.invalidateQueries({ queryKey: adminKeys.user(id) });
}

function invalidateRole(qc: QueryClient, id: number): void {
  void qc.invalidateQueries({ queryKey: adminKeys.roles() });
  void qc.invalidateQueries({ queryKey: adminKeys.role(id) });
}

// =============================================================================
// Users
// =============================================================================

export interface AdminUsersFilters {
  q?: string;
  active?: boolean;
}

export function useAdminUsers(filters: AdminUsersFilters = {}) {
  const params = new URLSearchParams();
  if (filters.q && filters.q.trim() !== '') params.set('q', filters.q.trim());
  if (filters.active !== undefined) params.set('active', String(filters.active));
  const qs = params.toString();
  const url = qs ? `/api/admin/users?${qs}` : '/api/admin/users';
  return useQuery({
    queryKey: adminKeys.usersList(filters),
    queryFn: ({ signal }) => api.get<AdminUsersListResponse>(url, signal),
  });
}

export function useAdminUser(id: number | null) {
  return useQuery({
    queryKey: adminKeys.user(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) => api.get<AdminUserResponse>(`/api/admin/users/${id}`, signal),
  });
}

export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAdminUserInput) =>
      api.post<CreateAdminUserResponse>('/api/admin/users', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

export interface UpdateAdminUserArgs extends UpdateAdminUserInput {
  id: number;
}

export function useUpdateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateAdminUserArgs) =>
      api.patch<AdminUserResponse>(`/api/admin/users/${id}`, patch),
    onSuccess: (_d, { id }) => invalidateUser(qc, id),
  });
}

export function useSetAdminUserRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role_ids }: { id: number; role_ids: number[] }) =>
      api.put<AdminUserResponse>(`/api/admin/users/${id}/roles`, { role_ids }),
    onSuccess: (_d, { id }) => invalidateUser(qc, id),
  });
}

export function useSetAdminUserPerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, person_id }: { id: number; person_id: number | null }) =>
      api.put<AdminUserResponse>(`/api/admin/users/${id}/person`, { person_id }),
    onSuccess: (_d, { id }) => invalidateUser(qc, id),
  });
}

export function useResetAdminUserPassword() {
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & ResetPasswordInput) =>
      api.post<ResetPasswordResponse>(`/api/admin/users/${id}/reset-password`, body),
  });
}

export function useRevokeAdminUserSessions() {
  return useMutation({
    mutationFn: (id: number) =>
      api.post<RevokeSessionsResponse>(`/api/admin/users/${id}/revoke-sessions`),
  });
}

export function useDeleteAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/admin/users/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
}

// =============================================================================
// Roles & permissions
// =============================================================================

export function usePermissions() {
  return useQuery({
    queryKey: adminKeys.permissions(),
    queryFn: ({ signal }) => api.get<PermissionsListResponse>('/api/admin/permissions', signal),
  });
}

export function useRoles() {
  return useQuery({
    queryKey: adminKeys.roles(),
    queryFn: ({ signal }) => api.get<RolesListResponse>('/api/admin/roles', signal),
  });
}

export function useRole(id: number | null) {
  return useQuery({
    queryKey: adminKeys.role(id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) => api.get<RoleResponse>(`/api/admin/roles/${id}`, signal),
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRoleInput) => api.post<RoleResponse>('/api/admin/roles', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.roles() });
    },
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number } & UpdateRoleInput) =>
      api.patch<RoleResponse>(`/api/admin/roles/${id}`, patch),
    onSuccess: (_d, { id }) => invalidateRole(qc, id),
  });
}

export function useSetRolePermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, permission_ids }: { id: number; permission_ids: number[] }) =>
      api.put<RoleResponse>(`/api/admin/roles/${id}/permissions`, { permission_ids }),
    onSuccess: (_d, { id }) => invalidateRole(qc, id),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/admin/roles/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.roles() });
    },
  });
}
